"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { FormHeader } from '@open-mercato/ui/backend/forms'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@open-mercato/ui/primitives/tabs'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { deleteCrud, updateCrud } from '@open-mercato/ui/backend/utils/crud'
import { type DriverDetail } from '../../../lib/driversTable'
import {
  DriversProfileTab,
  DriversQualificationsTab,
  DriversPwaAccessTab,
} from '../../../lib/detailSections'
import { DriverDialog, toDriverFormValues, type DriverMutationPayload } from '../../../lib/driverForm'

async function fetchDriverDetail(id: string): Promise<DriverDetail> {
  return readApiResultOrThrow<DriverDetail>(`/api/road/drivers/${id}`, undefined, {
    errorMessage: 'Failed to fetch driver detail.',
  })
}

export default function DriverDetailPage({ params }: { params: { id: string } }) {
  const t = useT()
  const router = useRouter()
  const scopeVersion = useOrganizationScopeVersion()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation({ contextId: `drivers-detail-${params.id}` })
  const [activeTab, setActiveTab] = React.useState('profile')
  const [deletePending, setDeletePending] = React.useState(false)
  const [editOpen, setEditOpen] = React.useState(false)
  const [editPending, setEditPending] = React.useState(false)

  const driverQuery = useQuery({
    queryKey: ['drivers', 'detail', params.id, scopeVersion],
    queryFn: () => fetchDriverDetail(params.id),
  })

  const refreshDriver = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['drivers', 'detail', params.id] })
    await queryClient.invalidateQueries({ queryKey: ['drivers', 'list'] })
  }, [params.id, queryClient])

  const driver = driverQuery.data ?? null

  const handleEdit = React.useCallback(async (payload: DriverMutationPayload) => {
    if (!driver) return
    setEditPending(true)
    try {
      await runMutation({
        context: { kind: 'road.driver', record: { id: driver.id } },
        mutationPayload: payload,
        operation: async () => {
          await updateCrud(`road/drivers/${driver.id}`, payload, {
            errorMessage: t('drivers.messages.updateError', 'Failed to update driver.'),
          })
        },
      })
      flash(t('drivers.messages.updateSuccess', 'Driver updated.'), 'success')
      setEditOpen(false)
      await refreshDriver()
    } finally {
      setEditPending(false)
    }
  }, [driver, refreshDriver, runMutation, t])

  const handleDelete = React.useCallback(async () => {
    if (!driver || deletePending) return
    const approved = await confirm({
      title: t('drivers.confirm.deleteTitle', 'Delete driver?'),
      text: t('drivers.confirm.deleteMessage', 'This will archive the driver and keep audit history intact.'),
      confirmText: t('drivers.actions.delete', 'Delete'),
      cancelText: t('common.cancel', 'Cancel'),
      variant: 'destructive',
    })
    if (!approved) return
    setDeletePending(true)
    try {
      await runMutation({
        context: { kind: 'road.driver', record: { id: driver.id } },
        mutationPayload: { id: driver.id },
        operation: async () => {
          await deleteCrud(`road/drivers/${driver.id}`, {
            errorMessage: t('drivers.messages.deleteError', 'Failed to delete driver.'),
          })
        },
      })
      flash(t('drivers.messages.deleteSuccess', 'Driver deleted.'), 'success')
      await refreshDriver()
      router.push('/backend/drivers')
    } catch {
      flash(t('drivers.messages.deleteError', 'Failed to delete driver.'), 'error')
    } finally {
      setDeletePending(false)
    }
  }, [confirm, deletePending, driver, refreshDriver, router, runMutation, t])

  if (driverQuery.isLoading && !driver) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('drivers.messages.loadingDetail', 'Loading driver detail…')} />
        </PageBody>
      </Page>
    )
  }

  if (driverQuery.isError || !driver) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage
            label={t('drivers.messages.loadErrorTitle', 'Could not load drivers')}
            description={t('drivers.messages.loadDetailError', 'The driver could not be loaded.')}
          />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <FormHeader
        mode="detail"
        backHref="/backend/drivers"
        entityTypeLabel={t('drivers.pages.list.title', 'Driver')}
        title={`${driver.lastName} ${driver.firstName}`}
        subtitle={driver.phone ?? driver.email ?? undefined}
        statusBadge={(
          <Badge variant={driver.isActive ? 'default' : 'muted'}>
            {driver.isActive
              ? t('drivers.detail.status.active', 'Active')
              : t('drivers.detail.status.inactive', 'Inactive')}
          </Badge>
        )}
        menuActions={[
          {
            id: 'edit',
            label: t('drivers.actions.editInline', 'Edit details'),
            onSelect: () => setEditOpen(true),
          },
        ]}
        onDelete={() => { void handleDelete() }}
        isDeleting={deletePending}
        deleteLabel={t('drivers.actions.delete', 'Delete')}
      />

      <PageBody>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="profile">{t('drivers.detail.tabs.profile', 'Profile')}</TabsTrigger>
            <TabsTrigger value="qualifications">{t('drivers.detail.tabs.qualifications', 'Qualifications')}</TabsTrigger>
            <TabsTrigger value="pwaAccess">{t('drivers.detail.tabs.pwaAccess', 'PWA Access')}</TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <DriversProfileTab driver={driver} t={t} />
          </TabsContent>
          <TabsContent value="qualifications">
            <DriversQualificationsTab driver={driver} t={t} />
          </TabsContent>
          <TabsContent value="pwaAccess">
            <DriversPwaAccessTab driver={driver} t={t} />
          </TabsContent>
        </Tabs>
      </PageBody>

      {ConfirmDialogElement}

      <DriverDialog
        open={editOpen}
        onOpenChange={(open) => { if (!editPending) setEditOpen(open) }}
        mode="edit"
        initialValues={toDriverFormValues(driver)}
        loading={editPending}
        onSubmit={handleEdit}
      />
    </Page>
  )
}
